import { forwardRef } from 'react';

import { Alert, Group, Loader, Select, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import type { Entity } from '@teo/backend';

import { useAuth } from '../../contexts/AuthContext';
import { errorMessageOf, readOf } from '../../lib/readOf';
import { trpc } from '../../lib/trpc';
import { id } from '../../utils/entityUtils';
import { StaleNotice } from './StaleNotice';

interface EntitySelectorProps {
  label?: string;
  placeholder?: string;
  value?: string | null;
  onChange?: (value: Entity | undefined) => void;
  error?: string;
  required?: boolean;
  clearable?: boolean;
  entityTypes: ('ownership' | 'portfolio' | 'property')[];
  showId?: boolean;
  'data-testid'?: string;
}
const EntityItem = forwardRef<HTMLDivElement, { entity: Entity; showId?: boolean }>(
  ({ entity, showId = true, ...others }, ref) => {
    return (
      <div ref={ref} {...others} data-testid={`entity-option-${id(entity)}`}>
        <Group gap="sm">
          <div>
            <Text size="sm" fw={500}>
              {entity.name}
            </Text>
            {showId && (
              <Text size="xs" c="dimmed">
                ID: {id(entity)}
              </Text>
            )}
          </div>
        </Group>
      </div>
    );
  },
);

export function EntitySelector({
  label,
  placeholder = 'Select an entity',
  value,
  onChange,
  error,
  required = false,
  clearable = true,
  entityTypes,
  showId = true,
  'data-testid': dataTestId,
}: EntitySelectorProps) {
  const { user } = useAuth();
  const allEntitiesQuery = useQuery({
    ...trpc.entities.getEntities.queryOptions(),
    enabled: entityTypes.length > 0 && !!user,
  });
  const allEntitiesRead = readOf(allEntitiesQuery);
  const { data: allEntities, isLoading } = allEntitiesQuery;

  if (allEntitiesRead.kind === 'failed') {
    return (
      <Alert color="red" title="Error loading entities">
        {errorMessageOf(allEntitiesRead.error) || 'Failed to load entities'}
      </Alert>
    );
  }

  /**
   * The levels asked for, or whatever the caller has if they hold none of them.
   *
   * The top bar asks for ownerships, which is right for someone who holds one.
   * A property manager granted a single property under an ownership they are
   * denied holds none: the list no longer offers that ownership, because it is
   * not theirs to choose. Filtering strictly would leave them with an empty
   * scope selector and no way to pick anything at all, so the selector falls
   * back to the levels they do have — for them the scope is that property, and
   * ownership-level surfaces correctly show nothing.
   */
  const available = allEntities || [];
  const atRequestedLevels = available.filter((entity: Entity) => entityTypes.includes(entity.type));
  const offered = atRequestedLevels.length > 0 ? atRequestedLevels : available;

  const entityOptions: Map<string, Entity> = offered.reduce(
    (acc: Map<string, Entity>, entity: Entity) => {
      acc.set(id(entity), entity);
      return acc;
    },
    new Map(),
  );

  const selectData = Array.from(entityOptions.entries()).map(([entityId, entity]) => ({
    value: entityId,
    label: showId ? `${entity.name} (${entityId})` : entity.name,
  }));
  return (
    <>
      <Select
        label={label}
        placeholder={placeholder}
        value={value ?? null}
        onChange={(value: string | null) =>
          onChange?.(value ? entityOptions.get(value) : undefined)
        }
        data={selectData}
        searchable
        clearable={clearable}
        required={required}
        error={error}
        rightSection={isLoading ? <Loader size={16} /> : undefined}
        maxDropdownHeight={300}
        comboboxProps={{ withinPortal: false }}
        renderOption={({ option }) => {
          const entityOption = entityOptions.get(option.value);
          return entityOption ? <EntityItem entity={entityOption} showId={showId} /> : null;
        }}
        clearButtonProps={{
          styles: {
            root: {
              color: 'var(--mantine-color-purple-4)',
            },
          },
        }}
        data-testid={dataTestId}
      />
      {allEntitiesRead.kind === 'ready' && allEntitiesRead.refreshFailed && <StaleNotice />}
    </>
  );
}
